import { useState, useEffect, useCallback } from 'react';
import { db } from 'config/firebase';
import {
  collection,
  query,
  getDocs,
  doc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';

/**
 * Hook to manage FC baseline data from Cart.com
 */
export function useFCBaseline() {
  const [baseline, setBaseline] = useState(new Map());
  const [baselineLoading, setBaselineLoading] = useState(false);
  const [baselineError, setBaselineError] = useState(null);

  // Load baseline data
  const loadBaseline = useCallback(async () => {
    try {
      setBaselineLoading(true);
      setBaselineError(null);

      const q = query(collection(db, 'fcBaseline'));
      const snapshot = await getDocs(q);

      const baselineMap = new Map();
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        baselineMap.set(data.sku, {
          sku: data.sku,
          fulfillmentCost: data.fulfillmentCost,
          title: data.title,
          quantity: data.quantity,
          fulfillmentCenter: data.fulfillmentCenter,
          category: data.category,
          importedAt: data.importedAt,
          source: data.source
        });
      });

      setBaseline(baselineMap);
    } catch (err) {
      console.error('Error loading baseline:', err);
      setBaselineError(err);
    } finally {
      setBaselineLoading(false);
    }
  }, []);

  // Upload/update baseline for a single SKU
  const updateBaselineSku = useCallback(async (sku, data) => {
    try {
      const docRef = doc(db, 'fcBaseline', sku);
      await setDoc(docRef, {
        sku,
        ...data,
        updatedAt: serverTimestamp()
      }, { merge: true });

      // Update local state
      setBaseline(prev => {
        const newMap = new Map(prev);
        newMap.set(sku, { sku, ...data });
        return newMap;
      });

      return { success: true };
    } catch (err) {
      console.error('Error updating baseline:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Get FC for a specific SKU
  const getBaselineFC = useCallback((sku) => {
    const data = baseline.get(sku);
    return data?.fulfillmentCost ?? null;
  }, [baseline]);

  // Initial load
  useEffect(() => {
    loadBaseline();
  }, [loadBaseline]);

  return {
    baseline,
    baselineLoading,
    baselineError,
    loadBaseline,
    updateBaselineSku,
    getBaselineFC
  };
}

export default useFCBaseline;
